import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { CreditCard, Eye, RefreshCw, CheckCircle2, XCircle, Clock } from 'lucide-react'
import { onlinePaymentsApi } from '@/lib/api'
import { formatCurrency, formatDate } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { PaymentStatus, GatewayType } from '@/types'

const statusConfig: Record<PaymentStatus, { icon: typeof CheckCircle2; color: string; label: string }> = {
  PENDING: { icon: Clock, color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', label: 'Pending' },
  PROCESSING: { icon: RefreshCw, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', label: 'Processing' },
  COMPLETED: { icon: CheckCircle2, color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Completed' },
  FAILED: { icon: XCircle, color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Failed' },
  REFUNDED: { icon: RefreshCw, color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200', label: 'Refunded' },
  CANCELLED: { icon: XCircle, color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200', label: 'Cancelled' },
}

const gatewayLabels: Record<GatewayType, string> = {
  STRIPE: 'Stripe',
  RAZORPAY: 'Razorpay',
  PAYPAL: 'PayPal',
}

export default function PaymentHistoryPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | 'ALL'>('ALL')
  const [gatewayFilter, setGatewayFilter] = useState<GatewayType | 'ALL'>('ALL')

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['online-payments', page, pageSize, statusFilter, gatewayFilter],
    queryFn: () => onlinePaymentsApi.getAll({
      page,
      page_size: pageSize,
      status: statusFilter !== 'ALL' ? statusFilter : undefined,
      gateway: gatewayFilter !== 'ALL' ? gatewayFilter : undefined,
    }),
  })

  const payments = data?.payments || []
  const total = data?.total || 0
  const totalPages = Math.ceil(total / pageSize)

  const clearFilters = () => {
    setStatusFilter('ALL')
    setGatewayFilter('ALL')
    setPage(1)
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="h-6 w-6" />
            {t('payments.paymentHistory', 'Payment History')}
          </h1>
          <p className="text-muted-foreground">
            {t('payments.paymentHistoryDescription', 'View all online payment transactions')}
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('common.refresh', 'Refresh')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('common.status', 'Status')}</label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as PaymentStatus | 'ALL'); setPage(1) }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Statuses</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="PROCESSING">Processing</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                  <SelectItem value="REFUNDED">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('payments.gateway', 'Gateway')}</label>
              <Select value={gatewayFilter} onValueChange={(v) => { setGatewayFilter(v as GatewayType | 'ALL'); setPage(1) }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Gateways</SelectItem>
                  <SelectItem value="STRIPE">Stripe</SelectItem>
                  <SelectItem value="RAZORPAY">Razorpay</SelectItem>
                  <SelectItem value="PAYPAL">PayPal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(statusFilter !== 'ALL' || gatewayFilter !== 'ALL') && (
              <Button variant="ghost" onClick={clearFilters}>
                {t('common.clearFilters', 'Clear Filters')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('common.date', 'Date')}</TableHead>
                <TableHead>{t('payments.gateway', 'Gateway')}</TableHead>
                <TableHead>{t('common.amount', 'Amount')}</TableHead>
                <TableHead>{t('common.status', 'Status')}</TableHead>
                <TableHead>{t('payments.payer', 'Payer')}</TableHead>
                <TableHead className="text-right">{t('common.actions', 'Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                    {t('common.loading', 'Loading...')}
                  </TableCell>
                </TableRow>
              ) : payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    {t('payments.noPayments', 'No payments found')}
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment) => {
                  const statusInfo = statusConfig[payment.status]
                  const StatusIcon = statusInfo.icon
                  return (
                    <TableRow key={payment.payment_id}>
                      <TableCell>{formatDate(payment.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{gatewayLabels[payment.gateway_type]}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(payment.amount, payment.currency)}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusInfo.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {payment.payer_email || payment.payer_name || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/billing/invoices/${payment.invoice_id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('common.showingOf', 'Showing {{from}} to {{to}} of {{total}} results', {
              from: (page - 1) * pageSize + 1,
              to: Math.min(page * pageSize, total),
              total,
            })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              {t('common.previous', 'Previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              {t('common.next', 'Next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

